<template>
    <div class="card" :class="typeToClass(unitType)">
        <div class="card-header">
            <div class="ribbon">
                <span class="quantity">
                    x {{ quantity }}
                </span>
                <div class="ribbon-edge left">
                </div>
                <div class="ribbon-edge right">
                </div>
            </div>
            <div class="coin">
                <img
                    class="unit-img"
                    :src="getAssetUrl(`@/assets/units/${imageName}`)"
                    :title="artAttributionValue"
                />
                <div class="decoy-coin-wrapper" v-if="decoyCoinType">
                    <img class="decoy-coin-img" :src="getAssetUrl(`@/assets/units/decoy_coin_${decoyCoinType.toLowerCase()}.png`)" />
                </div>
                <div class="poison-token-wrapper" v-if="poisonTokens.length">
                    <img
                        class="poison-token-img"
                        :src="getAssetUrl(`@/assets/poison_token_${unitType.toLowerCase()}.png`)"
                        v-for="(_poisonToken, i) in poisonTokens"
                        :key="`poison-token-${i}`"
                    />
                </div>
				<div class="cannonball-wrapper" v-if="cannonball">
					<img class="cannonball-img" :src="getAssetUrl('@/assets/cannonball.png')" />
                </div>
            </div>
            <div class="title" :class="{['multi-line']: typeToLabel(unitType).length > 1}">
                <div v-for="(word, i) in typeToLabel(unitType)"
                    :key="`label-${word}-${i}`"
                    :class="{['long-word']: word.length > 10 }"
                >
                    {{word}}
                </div>
				<span class="version" v-if="version">
					{{ version }}
				</span>
            </div>
            <div class="concept-attribution" v-if="conceptAttribution && !multipleAttributions">
                Creator: {{conceptAttribution}}
            </div>
			<div class="concept-attribution multiple" v-if="conceptAttribution && multipleAttributions">
                Creator: {{conceptAttribution[0]}}
				<div v-for="(name, i) in conceptAttribution.slice(1)" :key="`attr-${name}-${i}`">
					{{ name }}
				</div>
            </div>
        </div>
        <div class="card-content">
            <div class="card-grid-wrapper">
                <div class="card-grid">
                    <div class="card-grid-row" v-for="(row, i) in rows" :key="`row-${i}`"
                        :class="{
                                even: (i + 1) % 2 === 0,
                                odd: (i + 1) % 2 != 0
                            }">
                        <div class="card-tile"
                            v-for="(_hex, j) in hexes" :key="`hex-${i}${j}`"
                            :position="`${i},${j}`"
                            :class="{ colored: coloredTile(`${i},${j}`)}"
                        >
                            <div class="card-hexagon-outer">
                                <div class="card-hexagon-inner">
                                    <div class="card-hexagon-content"
                                        :class="{ ['multiple-units']: unitsInPosition(`${i},${j}`).length > 1 }"
                                    >
                                        <span class="card-unit"
                                            v-for="(unit, k) in unitsInPosition(`${i},${j}`)"
                                            :key="`card-unit-${unit.type}-${k}`"
                                            :class="{
                                                [unit.type]: true,
                                            }"
                                        >
										<ShockIcon
											v-if="unit.icon === 'shock'"
											class="card-unit-icon"
											:class="unit.icon"
										/>
                                        <fa-icon
											v-else-if="unit.icon"
                                            :icon="unit.icon"
                                            class="card-unit-icon"
                                            :class="unit.icon"
                                        />
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <fa-icon class="arrow" icon="long-arrow-alt-up"
                                v-for="(arrow, k) in elementsInPosition('arrows', `${i},${j}`)"
                                :key="`card-arrow-${i},${j}-${k}`"
                                :class="{
                                    [`angle-${arrow.angle}`]: true,
                                    [arrow.type]: true,
                                    [arrow.customClass]: arrow.customClass,
                                }"
                            />
                            <span class="line" v-for="(line, k) in elementsInPosition('lines', `${i},${j}`)"
                                :key="`card-line-${i},${j}-${k}`"
                                :class="{
                                    [`angle-${line.angle}`]: true,
                                    [line.type]: true,
                                    [line.customClass]: line.customClass,
                                }"
                            />
                            <div class="card-tile card-base"
                                v-for="(_base, k) in elementsInPosition('bases', `${i},${j}`)"
                                :key="`card-base-${i},${j}-${k}`"
                            >
                                <div class="card-hexagon-outer">
                                    <div class="card-hexagon-inner">
                                    </div>
                                </div>
                            </div>
                            <div class="card-fort"
                                v-for="(base, k) in elementsInPosition('forts', `${i},${j}`)"
                                :key="`card-fort-${i},${j}-${k}`">
                                <icon class="icon icon-fort" icon="fort" />
                            </div>
                            <img
                                class="mini-poison-token"
                                :src="getAssetUrl(`@/assets/poison_token_${unitType.toLowerCase()}.png`)"
                                v-for="(_, k) in elementsInPosition('miniPoisonTokens', `${i},${j}`)"
                                :key="`poison-token-${i},${j}-${k}`"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="tactic-description" v-if="this.$slots.tactic"
                :class="{
                    ['long-description']: tacticLength && tacticLength === 'long-description',
                    ['medium-description']: tacticLength && tacticLength === 'medium-description',
                }"
                >
                <span class="tactic-label">
					<strong>{{tacticType === 'siege' ? 'SIEGE ' : ''}}{{tacticType === 'sea' ? 'SEA ' : ''}}TACTIC:</strong>
				</span>
                <slot name="tactic" />
            </div>
            <div class="ribbon-proclamation" v-if="proclamationRibbon">
                <span class="dot">

                </span>
                <div class="ribbon-edge left">
                </div>
                <div class="ribbon-edge right">
                </div>
            </div>
        </div>
        <div class="card-footer" :class="{[cardFooterClass]: cardFooterClass}">
            <div class="footer-content attribute" v-if="this.$slots.description">
                <div class="footer-alert" v-if="this.$slots.description && descriptionLength !== 'long-description'">
                    <fa-icon :icon="descriptionIcon" v-if="descriptionIcon" />
                </div>
                <div
					v-if="this.$slots.description"
					class="footer-description"
                    :class="{[descriptionLength]: descriptionLength}"
                >
					<div class="description-content" v-if="this.$slots.description">
						<fa-icon
							v-if="descriptionIcon && descriptionLength === 'long-description'"
							:icon="descriptionIcon" class="icon"
						/>
						<slot name="description" />
					</div>
                </div>
            </div>
			<div class="footer-content restriction" v-if="this.$slots.restriction">
                <div class="footer-alert">
					<fa-icon icon="times-circle" class="icon" />
                </div>
				<div class="footer-description" :class="{[restrictionLength]: restrictionLength}">
					<slot name="restriction" />
				</div>
            </div>
            <div class="footer-spike">
            </div>
            <div class="footer-spike">
            </div>
            <div class="footer-spike">
            </div>
        </div>
    </div>
</template>

<script>
import { getAssetUrl } from '../../helpers/assets';
import ShockIcon from '../../assets/shock_icon.svg';

export default {
	components: {
		ShockIcon,
	},
    props: [
		'arrows',
		'artAttribution',
		'bases',
		'cannonball',
		'cardFooterClass',
		'coloredTiles',
		'conceptAttribution',
		'decoyCoinType',
		'descriptionIcon',
		'descriptionLength',
		'forts',
		'imageName',
		'lines',
		'miniMapRows',
		'miniPoisonTokens',
		'miniUnits',
		'poisonTokensCount',
		'proclamationRibbon',
		'quantity',
		'restrictionLength',
		'tacticLength',
		'tacticType',
		'unitType',
		'version'
    ],
    name: 'Card',
    data() {
        return {
            hexes: new Array(6),
            rows: new Array(this.miniMapRows || 4),
        };
    },
    computed: {
        poisonTokens() {
            return this.poisonTokensCount ? Array.from({ length: this.poisonTokensCount }, (_v, i) => i) : [];
        },
        artAttributionValue() {
            return this.artAttribution ? `Unit art by ${this.artAttribution}` : '';
        },
		multipleAttributions() {
			return this.conceptAttribution && typeof this.conceptAttribution === 'object';
		},
    },
    methods: {
        getAssetUrl,
        unitsInPosition(position) {
            return this.miniUnits.filter(unit => unit.position === position);
        },
        typeToClass(text) {
			text = text.replace(/_V\d+$/i, '');

            return text.split(/[^A-Z0-9]+/).map(word => word.toLowerCase()).join('-');
        },
        typeToLabel(text) {
			text = text.replace(/_V\d+$/i, '');

            return text.split(/[^A-Z]+/)
                .map(word => `${word.slice(0, 1)
                    .toUpperCase()}${word.slice(1).toLowerCase()}`)
                .filter(word => word.toLowerCase() !== 'nobility');
        },
        coloredTile(position) {
            return this.coloredTiles ? this.coloredTiles.includes(position) : false;
        },
        elementsInPosition(elementType, position) {
            return this[elementType] ? this[elementType].filter(element => element.position === position) : [];
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .card {
        height: 270px;
        width: 205px;
        background: $beige;
        overflow: hidden;
        position: relative;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        color: white;
        font-family: 'Avenir', 'Arial';
        text-align: left;

        &:before {
            content: '';
            width: 250px;
            height: 120px;
            background: $brown;
            border-radius: 125px / 60px;
            position: absolute;
            top: -50px;
            left: -25px;
            transform: rotate(10deg);
        }

        .card-header {
            position: relative;

            .title {
                padding: 30px 0 0 95px;
                font-family: 'IrishUncialfabeta';
				position: relative;

                &.multi-line {
                    padding-top: 20px;
                    margin-bottom: -7px;
                }

                .long-word {
                    font-size: .9em;
                }

				.version {
					position: absolute;
					right: 10px;
					bottom: 0;
					font-family: Avenir, Arial;
					font-size: .65rem;
					border-radius: 4px;
					padding: 0 4px;
				}
            }

            .concept-attribution {
                font-size: .6rem;
                position: absolute;
                right: 10px;
                top: 5px;
				text-align: right;

				&.multiple {
					font-size: .5rem;
				}
            }

            .coin {
                position: absolute;
                left: 16px;
                top: 16px;
                border-radius: 50%;
                box-shadow: -1px 1px 2px -1px $gray;
                z-index: 2;

                .unit-img {
                    display: block;
                    width: 70px;
                    height: auto;
                }
            }

            .ribbon {
                width: 35px;
                height: 115px;
                position: absolute;
                left: 22px;
                background: $pikeman;
                z-index: 1;

                .ribbon-edge {
                    width: 0;
                    height: 0;
                    position: absolute;
                    bottom: -6px;
                    border-color: $pikeman;

                    &.left {
                        border-top: 6.5px solid transparent;
                        border-bottom: 6.5px solid transparent;
                        border-left-width: 20px;
                        border-left-style: solid;
                    }

                    &.right {
                        border-top: 6.5px solid transparent;
                        border-bottom: 6.5px solid transparent;
                        border-right-width: 20px;
                        border-right-style: solid;
                        right: 0;
                    }
                }

                .quantity {
                    position: absolute;
                    bottom: 6px;
                    left: 50%;
                    transform: translateX(-50%);
                    display: block;
                    width: 100%;
                    text-align: center;
                }
            }

            .poison-token-wrapper {
                bottom: -10px;
                position: absolute;
                right: -8px;
                position: absolute;

                .poison-token-img {
                    $leftIncrement: 3.5;
                    $topIncrement: 5;
                    height: auto;
                    max-height: 25px;
                    max-width: 25px;
                    width: auto;
                    border: 1px solid $dark-gray-alt;
                    border-radius: 50%;

                    @for $i from 2 through 5 {
                        &:nth-of-type(#{$i}) {
                            position: absolute;
                            top: -#{$topIncrement}px;
                            left: #{$leftIncrement}px;
                            $leftIncrement: $leftIncrement + 3.5;
                            $topIncrement: $topIncrement + 5;
                        }
                    }
                }
            }

            .decoy-coin-wrapper {
                bottom: -12px;
                position: absolute;
                right: -10px;

                .decoy-coin-img {
                    height: auto;
                    max-height: 30px;
                    max-width: 30px;
                    width: auto;
                }
            }

			.cannonball-wrapper {
                bottom: -7px;
                position: absolute;
                right: -5px;

                .cannonball-img {
					height: auto;
                    max-height: 25px;
                    max-width: 25px;
                    width: auto;
                }
            }
        }

        .card-content {
            flex-grow: 1;
            position: relative;

            .tactic-description {
                color: $dark-gray;
                font-size: .65em;
                font-weight: normal;
                position: absolute;
                bottom: 9px;
                padding: 0 12.5px;

                &.long-description,
				&.long-restriction {
                    font-size: .53em;
                }

                &.medium-description {
                    font-size: .59em;
                }

				.tactic-label {
					margin-right: 2px;
				}
            }

            .ribbon-proclamation {
                width: 35px;
                height: 100px;
                position: absolute;
                left: 39px;
                top: 0px;
                background: white;
                opacity: .8;

                .ribbon-edge {
                    width: 0;
                    height: 0;
                    position: absolute;
                    bottom: -6px;
                    border-color: white;

                    &.left {
                        border-top: 6.5px solid transparent;
                        border-bottom: 6.5px solid transparent;
                        border-left-width: 20px;
                        border-left-style: solid;
                    }

                    &.right {
                        border-top: 6.5px solid transparent;
                        border-bottom: 6.5px solid transparent;
                        border-right-width: 20px;
                        border-right-style: solid;
                        right: 0;
                    }
                }

                .dot {
                    background-color: gray;
                    border-radius: 50%;
                    display: inline-block;
                    height: 15px;
                    width: 15px;
                    position: absolute;
                    bottom: 5.5px;
                    left: 50%;
                    transform: translateX(-50%);
                }
            }
        }

        .card-footer {
            background-color: $pikeman;
			box-sizing: border-box;
            position: relative;
            width: 100%;
			padding: 11px 12.5px;

			&.condensed {
				padding: 5px;
			}

            .footer-content {

                .footer-description {

                    &.long-description,
					&.long-restriction {
                        font-size: .6em;
                        padding: 0;

                        .icon {
                            height: 15px;
                            width: 15px;
                            vertical-align: middle;
                        }
                    }

					&.long-restriction {
                        font-size: .6em;
                        padding-left: 7px;
					}

                    &.medium-description {
                        font-size: .65em;
                    }
                }
            }

            .footer-content {
                display: flex;

                .footer-alert {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .footer-description {
                    padding-left: 7px;
                    font-size: .7em;
                    font-weight: 500;
                }
            }

            .footer-spike {
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-bottom-width: 4px;
                border-bottom-style: solid;
                border-bottom-color: $pikeman;
                position: absolute;
                top: -4px;
                left: 40px;

                &:nth-of-type(2) {
                    border-left: 7px solid transparent;
                    border-right: 7px solid transparent;
                    border-bottom-width: 5px;
                    left: 57px;
                    top: -5px;
                }

                &:nth-of-type(3) {
                    border-left: 8px solid transparent;
                    border-right: 8px solid transparent;
                    border-bottom-width: 5px;
                    left: 82%;
                    top: -5px;
                    top: -5px;
                }
            }
        }

        .card-grid-wrapper {
            position: relative;
            width: 70%;
            margin: 0 auto;

            .card-grid {
                margin-top: 35px;

                .card-grid-row {
                    display: flex;
                    justify-content: flex-end;
                    position: absolute;
                    width: 100%;

                    $increment: 0;

                    @for $i from 1 through 4 {
                        &:nth-of-type(#{$i}) {
                            top: #{$increment}px;
                            $increment: $increment + 18;
                        }
                    }

                    &.odd {
                        left: 10px;
                    }

                    &.even {

                        .card-tile:not(.card-base) {

                            &:first-of-type {

                                .card-hexagon-inner {
                                    visibility: hidden;
                                }
                            }
                        }
                    }
                }
            }

            .card-tile {
                height: 20px;
                width: 12.5%;
                margin: 0;
                padding: 0;
                position: relative;

                &:not(:last-of-type) {
                    margin-right: 2px;
                }

                .card-hexagon-outer {
                    height: 100%;
                    width: 100%;
                    transform: rotate(-60deg) skewY(30deg);
                    overflow: hidden;
                    position: relative;
                    visibility: hidden;
                }

                .card-hexagon-inner {
                    height: 100%;
                    width: 100%;
                    background-color: $card-tile;
                    transform: skewY(-30deg) rotate(60deg);
                    position: absolute;
                    top: 0;
                    left: 0;
                    visibility: visible;
                    box-sizing: border-box;
                }

                .card-hexagon-content {
                    text-align: center;

                    &.multiple-units {

                        .card-unit {

                            &.opponent {

                                &:first-of-type {
                                    background-color: $dark-tile-shaded;
                                }
                            }

                            &:nth-of-type(2) {
                                top: 54%;
                                left: 60%;
                            }
                        }
                    }
                }

                &.card-base {
                    height: 15.5px;
                    left: 50%;
                    position: absolute;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    width: 13.5px;
                    visibility: visible;

                    .card-hexagon-outer {
                        visibility: visible;
                    }

                    .card-hexagon-inner {
                        visibility: visible;
                        background-color: $medium-gray;
                    }
                }

                .arrow {
                    z-index: 1;
                    position: absolute;
                    top: 50%;
                    left: 50%;

                    &.attack {
                        color: $red;
                    }

                    &.move {
                        color: $medium-brown;
                    }

                    &.angle-32 {
                        transform: translate(44%, -126%) rotate(32deg);
                    }

                    &.angle-90 {
                        transform: translate(122%, -52%) rotate(90deg);
                    }

                    &.angle-150 {
                        transform: translate(43%, 21%) rotate(150deg);
                    }

                    &.angle-150-alt {
                        transform: translate(2%, -13%) rotate(150deg);
                    }

					&.angle-180 {
                        transform: translate(122%, -52%) rotate(180deg);
                    }

                    &.angle-211 {
                        transform: translate(-138%, 24%) rotate(211deg);
                    }

                    &.angle-211-alt {
                        transform: translate(-69%, -19%) rotate(211deg);
                    }

                    &.angle-270 {
                        transform: translate(-225%, -52%) rotate(270deg);
                    }

                    &.angle-328 {
                        transform: translate(-135%, -126%) rotate(328deg);
                    }

                    &.sapper {
                        transform: translate(18%, -92%) rotate(32deg);
                    }
                }

                .line {
                    z-index: 1;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    height: 19px;
                    width: 0;
                    border-right-width: 3px;
                    border-right-style: solid;
                    display: inline-block;

                    &.attack {
                        border-color: $red;
                    }

                    &.move {
                        border-color: $medium-brown;
                    }

                    &.angle-32 {
                        transform: translate(220%, -126%) rotate(32deg);

                        &.trebuchet {
                            height: 20px;
                            transform: translate(239%, -130%) rotate(32deg);
                        }
                    }

                    &.angle-90 {
                        transform: translate(16px, -52%) rotate(90deg);

                        &.skirmisher {
                            transform: translate(8px, -52%) rotate(90deg);
                        }
                    }

                    &.angle-150 {
                        transform: translate(5.5px, 27%) rotate(150deg);
                        height: 15px;
                    }

                    &.angle-270 {
                        transform: translate(-225%, -52%) rotate(270deg);

                        &.trebuchet {
                            transform: translate(-180%, -52%) rotate(270deg);
                        }
                    }

                    &.angle-328 {
                        transform: translate(-9.7px, -133%) rotate(328deg);
                    }
                }

                .mini-poison-token {
                    position: absolute;
                    z-index: 100;
                    height: 10px;
                    width: 10px;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }

                .card-fort {
                    background: $dark-brown;
                    width: 13px;
                    height: 13px;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border: 1px solid white;
                    border-radius: 50%;

                    .icon-fort {
                        width: 8px;
                        fill: white;
                        height: auto;
                        position: absolute;
                        left: 50%;
                        top: 50%;
                        -webkit-transform: translate(-50%, -50%);
                        transform: translate(-50%, -50%);
                    }
                }
            }

            .card-unit {
                width: 13px;
                height: 13px;
                border-radius: 50%;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                line-height: 13px;
                z-index: 10;

                &.friendly {
                    background-color: white;
                }

				&.cannonball {
					background-color: black;
					width: 11px;
					height: 11px;
				}

                &.opponent {
                    background-color: $dark-tile;

                    .card-unit-icon {

						&.shock {
                            fill: white;
							position: absolute;
							left: 50%;
							top: 50%;
							transform: translate(-50%, -50%);
                        }
                    }
                }
            }

            .card-unit-icon {
                color: $red;
                height: 9px;
                width: 9px;
                vertical-align: middle;

                &.plus {
                    color: white;
                }

                &.shock {
                    color: $dark-gray;
                }

                &.coins {
                    color: $burnished-gold;
                }
            }
        }

        &.alchemist {

            .card-footer {
                background-color: $alchemist;

                .footer-spike {
                    border-bottom-color: $alchemist;
                }
            }

            .ribbon {
                background: $alchemist;

                .ribbon-edge {
                    border-color: $alchemist;
                }
            }

            .self {
                background-color: $alchemist;
            }

            .card-grid-wrapper {

                .card-tile {

                    .arrow {

                        &.angle-90 {
                            transform: translate(97%, -52%) rotate(90deg);
                        }

                        &.angle-270 {
                            transform: translate(-205%, -52%) rotate(270deg);
                        }
                    }
                }
            }
        }

        &.apprentice {

            .card-footer {
                background-color: $apprentice;

                .footer-spike {
                    border-bottom-color: $apprentice;
                }
            }

            .ribbon {
                background: $apprentice;

                .ribbon-edge {
                    border-color: $apprentice;
                }
            }

            .self {
                background-color: $apprentice-light;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $apprentice-dark;
                    }
                }
            }
        }

        &.archer {

            .card-footer {
                background-color: $archer;

                .footer-spike {
                    border-bottom-color: $archer;
                }
            }

            .ribbon {
                background: $archer;

                .ribbon-edge {
                    border-color: $archer;
                }
            }

            .self {
                background-color: $archer;
            }
        }

        &.assassin {

            .card-footer {
                background-color: $assassin;

                .footer-spike {
                    border-bottom-color: $assassin;
                }
            }

            .ribbon {
                background: $assassin;

                .ribbon-edge {
                    border-color: $assassin;
                }
            }

            .self {
                background-color: $assassin;
            }

            .ribbon-proclamation {

                .dot {
                    background-color: $assassin;
                }
            }
        }

        &.bannerman {

            .card-footer {
                background-color: $bannerman;

                .footer-spike {
                    border-bottom-color: $bannerman;
                }
            }

            .ribbon {
                background: $bannerman;

                .ribbon-edge {
                    border-color: $bannerman;
                }
            }

            .self {
                background-color: $bannerman;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $bannerman-light;
                    }
                }
            }
        }

        &.berserker {

            .card-footer {
                background-color: $berserker;

                .footer-spike {
                    border-bottom-color: $berserker;
                }
            }

            .ribbon {
                background: $berserker;

                .ribbon-edge {
                    border-color: $berserker;
                }
            }

            .self {
                background-color: $berserker;
            }

            .card-hexagon-content {

                &.multiple-units {

                    .card-unit {

                        &:nth-of-type(2) {
                            background-color: $berserker-light;
                            left: 42%;
                            top: 41%;
                        }
                    }
                }
            }

			.card-header {

				.version {
					right: 5px;
					background: $berserker;
				}
			}
        }

        &.bishop {

            .card-footer {
                background-color: $bishop;

                .footer-spike {
                    border-bottom-color: $bishop;
                }
            }

            .ribbon {
                background: $bishop;

                .ribbon-edge {
                    border-color: $bishop;
                }
            }

            .self {
                background-color: $bishop;
            }
        }

        &.saboteur {

            .card-footer {
                background-color: $saboteur;

                .footer-spike {
                    border-bottom-color: $saboteur;
                }
            }

            .ribbon {
                background: $saboteur;

                .ribbon-edge {
                    border-color: $saboteur;
                }
            }

            .self {
                background-color: $saboteur;
            }

            .ribbon-proclamation {

                .dot {
                    background-color: $saboteur;
                }
            }
        }

        &.cavalry {

            .card-footer {
                background-color: $cavalry;

                .footer-spike {
                    border-bottom-color: $cavalry;
                }
            }

            .ribbon {
                background: $cavalry;

                .ribbon-edge {
                    border-color: $cavalry;
                }
            }

            .self {
                background-color: $cavalry;
            }
        }

        &.crossbowman {

            .card-footer {
                background-color: $crossbowman;

                .footer-spike {
                    border-bottom-color: $crossbowman;
                }
            }

            .ribbon {
                background: $crossbowman;

                .ribbon-edge {
                    border-color: $crossbowman;
                }
            }

            .self {
                background-color: $crossbowman;
            }
        }

        &.earl {

            .card-footer {
                background-color: $earl;

                .footer-spike {
                    border-bottom-color: $earl;
                }
            }

            .ribbon {
                background: $earl;

                .ribbon-edge {
                    border-color: $earl;
                }
            }

            .self {
                background-color: $earl;
            }

            .ribbon-proclamation {

                .dot {
                    background-color: $earl;
                }
            }
        }

        &.emissary {

            .card-footer {
                background-color: $emissary;

                .footer-spike {
                    border-bottom-color: $emissary;
                }
            }

            .ribbon {
                background: $emissary;

                .ribbon-edge {
                    border-color: $emissary;
                }
            }

            .self {
                background-color: $emissary-dark;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $emissary-light;
                    }
                }
            }

			.card-header {

				.version {
					background: $emissary;
				}
			}

        }

        &.ensign {

            .card-footer {
                background-color: $ensign;

                .footer-spike {
                    border-bottom-color: $ensign;
                }
            }

            .ribbon {
                background: $ensign;

                .ribbon-edge {
                    border-color: $ensign;
                }
            }

            .self {
                background-color: $ensign-dark;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $ensign;
                    }
                }
            }
        }

        &.footman {

            .card-footer {
                background-color: $footman;

                .footer-spike {
                    border-bottom-color: $footman;
                }
            }

            .ribbon {
                background: $footman;

                .ribbon-edge {
                    border-color: $footman;
                }
            }

            .self {
                background-color: $footman;
            }

			.card-header {

				.version {
					background: $footman;
				}
			}
        }

        &.herald {

            .card-footer {
                background-color: $herald;

                .footer-spike {
                    border-bottom-color: $herald;
                }
            }

            .ribbon {
                background: $herald;

                .ribbon-edge {
                    border-color: $herald;
                }
            }

            .self {
                background-color: $herald;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $herald-light;
                    }
                }
            }

            .ribbon-proclamation {

                .dot {
                    background-color: $herald;
                }
            }
        }

        &.heavy-cavalry {

            .card-footer {
                background-color: $heavy-cavalry;

                .footer-spike {
                    border-bottom-color: $heavy-cavalry;
                }
            }

            .ribbon {
                background: $heavy-cavalry;

                .ribbon-edge {
                    border-color: $heavy-cavalry;
                }
            }

            .self {
                background-color: $heavy-cavalry;
            }
        }

        &.infiltrator {

            .card-footer {
                background-color: $infiltrator;

                .footer-spike {
                    border-bottom-color: $infiltrator;
                }
            }

            .ribbon {
                background: $infiltrator;

                .ribbon-edge {
                    border-color: $infiltrator;
                }
            }

            .self {
                background-color: $infiltrator;
            }

            .ribbon-proclamation {

                .dot {
                    background-color: $infiltrator;
                }
            }
        }

        &.knight {

            .card-footer {
                background-color: $knight;

                .footer-spike {
                    border-bottom-color: $knight;
                }
            }

            .ribbon {
                background: $knight;

                .ribbon-edge {
                    border-color: $knight;
                }
            }

            .self {
                background-color: $knight;
            }
        }

        &.lancer {

            .card-footer {
                background-color: $lancer;

                .footer-spike {
                    border-bottom-color: $lancer;
                }
            }

            .ribbon {
                background: $lancer;

                .ribbon-edge {
                    border-color: $lancer;
                }
            }

            .self {
                background-color: $lancer;
            }
        }

        &.light-cavalry {

            .card-footer {
                background-color: $light-cavalry;

                .footer-spike {
                    border-bottom-color: $light-cavalry;
                }
            }

            .ribbon {
                background: $light-cavalry;

                .ribbon-edge {
                    border-color: $light-cavalry;
                }
            }

            .self {
                background-color: $light-cavalry;
            }
        }

        &.marshal {

            .card-footer {
                background-color: $marshal;

                .footer-spike {
                    border-bottom-color: $marshal;
                }
            }

            .ribbon {
                background: $marshal;

                .ribbon-edge {
                    border-color: $marshal;
                }
            }

            .self {
                background-color: $marshal-dark;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $marshal;
                    }
                }
            }
        }

        &.mercenary {

            .card-footer {
                background-color: $mercenary;

                .footer-spike {
                    border-bottom-color: $mercenary;
                }
            }

            .ribbon {
                background: $mercenary;

                .ribbon-edge {
                    border-color: $mercenary;
                }
            }

            .self {
                background-color: $mercenary;
            }
        }

		&.corsair {

			.card-footer {
				background-color: $corsair;

				.footer-spike {
					border-bottom-color: $corsair;
				}
			}

			.ribbon {
				background: $corsair;

				.ribbon-edge {
					border-color: $corsair;
				}
			}

			.self {
				background-color: $corsair;
			}

			.card-grid-wrapper .card-tile .arrow.angle-90 {
				transform: translate(41%, -52%) rotate(90deg);
			}

			.card-hexagon-content {

				&.multiple-units {

					.card-unit {

						&:nth-of-type(2) {
							background-color: $corsair-light;
							left: 42%;
							top: 41%;
						}
					}
				}
			}
		}

        &.overlord {

            .card-footer {
                background-color: $overlord;

                .footer-spike {
                    border-bottom-color: $overlord;
                }
            }

            .ribbon {
                background: $overlord;

                .ribbon-edge {
                    border-color: $overlord;
                }
            }

            .self {
                background-color: $overlord;
            }

            .card-hexagon-content {

                &.multiple-units {

                    .card-unit {

                        &:nth-of-type(2) {
                            background-color: $overlord-light;
                            left: 42%;
                            top: 41%;
                        }
                    }
                }
            }
        }

        &.pikeman {

            .card-footer {
                background-color: $pikeman;
            }

            .ribbon {
                background: $pikeman;

                .ribbon-edge {
                    border-color: $pikeman;
                }
            }

            .self {
                background-color: $pikeman;
            }
        }

        &.pirate {

			.card-footer {
				background-color: $pirate;

				.footer-content {

					.footer-description {
						font-size: .62em;
					}
				}


				.footer-spike {
                    border-bottom-color: $pirate;
                }
			}

			.ribbon {
				background: $pirate;

				.ribbon-edge {
					border-color: $pirate;
				}
			}

			.self {
				background-color: $pirate;
			}

			.card-tile {

				&.colored {

					.card-hexagon-inner {
						background-color: $dark-tile;
					}
				}
			}
		}

        &.pitch-thrower {

            .card-footer {
                background-color: $pitch-thrower;
                padding: 11px 12.5px;

                .footer-spike {
                    border-bottom-color: $pitch-thrower;
                }
            }

            .ribbon {
                background: $pitch-thrower;

                .ribbon-edge {
                    border-color: $pitch-thrower;
                }
            }

            .self {
                background-color: $pitch-thrower;
            }

            .footer-content {

                .footer-description {

                    &.long-description,
					&.long-restriction {
                        font-size: .55em;
                    }
                }
            }
        }

        &.raider {

            .card-footer {
                background-color: $raider;

                .footer-spike {
                    border-bottom-color: $raider;
                }
            }

            .ribbon {
                background: $raider;

                .ribbon-edge {
                    border-color: $raider;
                }
            }

            .self {
                background-color: $raider;
            }
        }

		&.hun {

			.card-footer {
				background-color: $hun;

				.footer-spike {
					border-bottom-color: $hun;
				}
			}

			.ribbon {
				background: $hun;

				.ribbon-edge {
					border-color: $hun;
				}
			}

			.self {
				background-color: $hun;
				z-index: 100;
			}

			.card-hexagon-content {

				&.multiple-units {

					.card-unit {

						&:nth-of-type(2) {
							background-color: $hun-light;
							left: 42%;
							top: 41%;
						}
					}
				}
			}

			.card-grid-wrapper .card-tile .line.attack {
				transform: translate(14px, -52%) rotate(90deg);
				height: 14px;
			}

			.card-grid-wrapper .card-tile .arrow.angle-150 {
				transform: translate(6%, 16%) rotate(150deg);
			}

			.card-grid-wrapper .card-tile .arrow.angle-32 {
				transform: translate(0, -90%) rotate(32deg);
			}

			.card-grid-wrapper .card-tile .arrow.angle-90 {
				transform: translate(0, -52%) rotate(90deg)
			}
		}


		&.admiral {

			.card-footer {
				background-color: $admiral;

				.footer-spike {
					border-bottom-color: $admiral;
				}
			}

			.ribbon {
				background: $admiral;

				.ribbon-edge {
					border-color: $admiral;
				}
			}

			.self {
				background-color: $admiral;
			}


            .card-tile {

				&.colored {

					.card-hexagon-inner {
						background-color: $admiral-light;
					}
				}
			}
		}

        &.rearguard {

            .card-footer {
                background-color: $rearguard;

                .footer-spike {
                    border-bottom-color: $rearguard;
                }
            }

            .ribbon {
                background: $rearguard;

                .ribbon-edge {
                    border-color: $rearguard;
                }
            }

            .self {
                background-color: $rearguard;
            }
            .card-hexagon-content {

                &.multiple-units {

                    .card-unit {

                        &:nth-of-type(2) {
                            background-color: $rearguard-light;
                            left: 42%;
                            top: 41%;
                        }
                    }
                }
            }
        }

        &.royal-guard,
        &.royal-guard-nobility {

            .card-footer {
                background-color: $royal-guard;

                .footer-spike {
                    border-bottom-color: $royal-guard;
                }
            }

            .ribbon {
                background: $royal-guard;

                .ribbon-edge {
                    border-color: $royal-guard;
                }
            }

            .self {
                background-color: $royal-guard;
            }
        }

        &.sapper {

            .card-footer {
                background-color: $sapper;

                .footer-spike {
                    border-bottom-color: $sapper;
                }
            }

            .ribbon {
                background: $sapper;

                .ribbon-edge {
                    border-color: $sapper;
                }
            }

            .self {
                background-color: $sapper;
            }
        }

        &.scout {

            .card-footer {
                background-color: $scout;

                .footer-spike {
                    border-bottom-color: $scout;
                }
            }

            .ribbon {
                background: $scout;

                .ribbon-edge {
                    border-color: $scout;
                }
            }

            .self {
                background-color: $scout;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $scout-light;
                    }
                }
            }
        }

        &.siege-tower {

            .card-footer {
                background-color: $siege-tower;

                .footer-spike {
                    border-bottom-color: $siege-tower;
                }
            }

            .ribbon {
                background: $siege-tower;

                .ribbon-edge {
                    border-color: $siege-tower;
                }
            }

            .self {
                background-color: $siege-tower;
            }
        }

        &.skirmisher {

            .card-footer {
                background-color: $skirmisher;

                .footer-spike {
                    border-bottom-color: $skirmisher;
                }
            }

            .ribbon {
                background: $skirmisher;

                .ribbon-edge {
                    border-color: $skirmisher;
                }
            }

            .self {
                background-color: $skirmisher;
            }

            .ribbon-proclamation {

                .dot {
                    background-color: $skirmisher;
                }
            }
        }

        &.trebuchet {

            .card-footer {
                background-color: $trebuchet;

                .footer-spike {
                    border-bottom-color: $trebuchet;
                }
            }

            .ribbon {
                background: $trebuchet;

                .ribbon-edge {
                    border-color: $trebuchet;
                }
            }

            .self {
                background-color: $trebuchet;
            }

			.card-header {

				.version {
					right: 3px;
					background: $trebuchet;
				}
			}
        }

        &.swordsman {

            .card-footer {
                background-color: $swordsman;

                .footer-spike {
                    border-bottom-color: $swordsman;
                }
            }

            .ribbon {
                background: $swordsman;

                .ribbon-edge {
                    border-color: $swordsman;
                }
            }

            .self {
                background-color: $swordsman;
            }
        }

        &.vanguard {

            .card-footer {
                background-color: $vanguard;

                .footer-spike {
                    border-bottom-color: $vanguard;
                }
            }

            .ribbon {
                background: $vanguard;

                .ribbon-edge {
                    border-color: $vanguard;
                }
            }

            .self {
                background-color: $vanguard;
            }
        }

        &.war-drummer {

            .card-footer {
                background-color: $war-drummer;

                .footer-spike {
                    border-bottom-color: $war-drummer;
                }

                .footer-content {

                    .footer-description {

                        &.long-description,
						&.long-restriction {
                            font-size: .5em;
                        }

						&.medium-description {
							font-size: .7em;
						}
                    }
                }
            }

            .ribbon {
                background: $war-drummer;

                .ribbon-edge {
                    border-color: $war-drummer;
                }
            }

            .self {
                background-color: $war-drummer;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $war-drummer;
                    }
                }
            }
        }

        &.war-wagon {

            .card-footer {
                background-color: $war-wagon;

                .footer-spike {
                    border-bottom-color: $war-wagon;
                }

                .footer-content {

                    .footer-description {

                        &.long-description,
						&.long-restriction {
                            font-size: .5em;
                        }
                    }
                }
            }

            .ribbon {
                background: $war-wagon;

                .ribbon-edge {
                    border-color: $war-wagon;
                }
            }

            .self {
                background-color: $war-wagon;
            }

            .card-tile {

                &.colored {

                    .card-hexagon-inner {
                        background-color: $war-wagon-light;
                    }
                }
            }
        }

        &.warlord{

            .card-footer {
                background-color: $warlord;

                .footer-spike {
                    border-bottom-color: $warlord;
                }
            }

            .ribbon {
                background: $warlord;

                .ribbon-edge {
                    border-color: $warlord;
                }
            }

            .self {
                background-color: $warlord;
            }
        }

        &.warrior-priest {

            .card-footer {
                background-color: $warrior-priest;

                .footer-spike {
                    border-bottom-color: $warrior-priest;
                }
            }

            .ribbon {
                background: $warrior-priest;

                .ribbon-edge {
                    border-color: $warrior-priest;
                }
            }

            .self {
                background-color: $warrior-priest;
            }

			.card-header {

				.version {
					background: $warrior-priest;
				}
			}
        }

		&.longboat {

			.card-footer {
				background-color: $longboat;
				font-size: .94rem;

				.footer-content {

					&.attribute {
						margin-bottom: 7.5px;
					}
				}

				.footer-spike {
					border-bottom-color: $longboat;
				}
			}

			.ribbon {
				background: $longboat;

				.ribbon-edge {
					border-color: $longboat;
				}
			}

			.self {
				background-color: $longboat;

				&:not(:first-of-type) {
					background-color: $archer;
				}
			}

			.card-tile {

				&.colored {

					// .card-hexagon-inner {
					// 	background-color: $dark-tile;
					// }

					.card-hexagon-inner {
						background-color: $longboat-light;
					}
				}
			}
		}
    }

</style>
